import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssigneeInfo, ButtonsData, DynamicWidgetConfig, ListData, Message, TemplateData, VisitorInfo, WidgetConfig, WidgetState, WidgetView } from '../types';
import { PreChatForm } from './PreChatForm';
import { getStoredVisitorData } from '../hooks/useSocket';
import { MediaContent } from '../shared/MediaContent';
import { useMediaComposer } from '../shared/useMediaComposer';
import type { PushSubscriptionPayload } from '../lib/push';
import { EmbedPushBanner } from './EmbedPushBanner';

// ============================================
// Public Props Interface
// ============================================

export interface ChatContentProps {
  config: WidgetConfig;
  state: WidgetState;
  messages: Message[];
  dynamicConfig?: DynamicWidgetConfig | null;
  assigneeInfo?: AssigneeInfo | null;
  configLoaded?: boolean;
  chatReady?: boolean;
  onSendMessage: (message: string) => void;
  onSendMedia?: (file: File) => Promise<void> | void;
  onRegisterPush?: (subscription: PushSubscriptionPayload) => Promise<boolean> | boolean;
  onConnect: (visitorInfo: VisitorInfo) => void;
  onEndConversation: () => void;
  /** Se true, renderiza botões Minimizar/Fechar no header (modo FAB). Default false. */
  showCloseButton?: boolean;
  /** Chamado ao clicar em Minimizar (modo FAB). */
  onClose?: () => void;
  /** Classe adicional de posição (ex: " tw-window--bottom-left"). Default vazio. */
  windowPosClass?: string;
  /** Se true, anima fechamento. Default false. */
  isClosing?: boolean;
  /** Callback chamado após encerrar + fechar no modal de confirmação (modo FAB). */
  onAfterEndConversation?: () => void;
}

// ============================================
// Helper: Darken Color
// ============================================

function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent));
  const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document']);
function isMediaMessage(t: string): boolean {
  return MEDIA_TYPES.has(t);
}

// ============================================
// SVG Icons
// ============================================

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MinimizeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.15" />
  </svg>
);

const PaperclipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const HeadphonesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

const WelcomeIcon = ({ color }: { color: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="9" y1="10" x2="9" y2="10" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="12" y1="10" x2="12" y2="10" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="15" y1="10" x2="15" y2="10" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

// ============================================
// Message Status Icons
// ============================================

const MessageStatus = ({ status }: { status?: string }) => {
  if (!status) return null;

  switch (status) {
    case 'sending':
      return (
        <span className="tw-message-status tw-status-sending" title="Enviando">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </span>
      );
    case 'sent':
      return (
        <span className="tw-message-status tw-status-sent" title="Enviado">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      );
    case 'delivered':
      return (
        <span className="tw-message-status tw-status-delivered" title="Entregue">
          <svg width="16" height="14" viewBox="0 0 28 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 6 9 17 4 12" />
            <polyline points="24 6 15 17 12 14" />
          </svg>
        </span>
      );
    case 'read':
      return (
        <span className="tw-message-status tw-status-read" title="Lido">
          <svg width="16" height="14" viewBox="0 0 28 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 6 9 17 4 12" />
            <polyline points="24 6 15 17 12 14" />
          </svg>
        </span>
      );
    case 'failed':
      return (
        <span className="tw-message-status tw-status-failed" title="Falhou">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </span>
      );
    default:
      return null;
  }
};

// ============================================
// Interactive Message Component
// ============================================

const InteractiveMessage = ({
  message,
  onSendMessage
}: {
  message: Message;
  onSendMessage: (text: string) => void;
}) => {
  const [listExpanded, setListExpanded] = useState(false);
  const data = message.interactiveData;

  if (!data) {
    return <div className="tw-msg-content">{message.content}</div>;
  }

  // Buttons
  if (data.type === 'buttons') {
    const bd = data as ButtonsData;
    return (
      <div>
        <div className="tw-msg-content">{bd.text || message.content}</div>
        {bd.footer && <div className="tw-interactive-footer">{bd.footer}</div>}
        <div style={{ marginTop: '6px' }}>
          {bd.buttons.map((btn) => (
            <button
              key={btn.id}
              className="tw-interactive-btn"
              onClick={() => onSendMessage(btn.text)}
            >
              {btn.text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // List
  if (data.type === 'list') {
    const ld = data as ListData;
    return (
      <div>
        <div className="tw-msg-content">{ld.text || message.content}</div>
        {ld.footer && <div className="tw-interactive-footer">{ld.footer}</div>}
        <button
          className="tw-interactive-btn"
          style={{ marginTop: '6px' }}
          onClick={() => setListExpanded(!listExpanded)}
        >
          {ld.buttonText || 'Menu'}
        </button>
        {listExpanded && (
          <div style={{ marginTop: '6px' }}>
            {ld.sections.map((section, si) => (
              <div key={si} style={{ marginBottom: '6px' }}>
                <div className="tw-interactive-section-title">
                  {section.title}
                </div>
                {section.rows.map((row) => (
                  <button
                    key={row.id}
                    className="tw-interactive-list-item"
                    onClick={() => onSendMessage(row.title)}
                  >
                    <div className="tw-interactive-list-item-title">{row.title}</div>
                    {row.description && (
                      <div className="tw-interactive-list-item-desc">{row.description}</div>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Template
  if (data.type === 'template') {
    const td = data as TemplateData;
    return (
      <div>
        <div className="tw-msg-content">{td.text || message.content}</div>
        <div style={{ marginTop: '6px' }}>
          {td.buttons.map((btn, i) => {
            if (btn.type === 'url' && btn.url) {
              return (
                <a
                  key={i}
                  href={btn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-interactive-btn"
                >
                  {btn.text} ↗
                </a>
              );
            }
            if (btn.type === 'call' && btn.phoneNumber) {
              return (
                <a
                  key={i}
                  href={`tel:${btn.phoneNumber}`}
                  className="tw-interactive-btn"
                >
                  {btn.text}
                </a>
              );
            }
            // quick_reply
            return (
              <button
                key={i}
                className="tw-interactive-btn"
                onClick={() => onSendMessage(btn.text)}
              >
                {btn.text}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Fallback: plain content
  return <div className="tw-msg-content">{message.content}</div>;
};

// ============================================
// Message Grouping Helper
// ============================================

interface GroupedMessage extends Message {
  isGrouped: boolean;
  isLastInGroup: boolean;
  isFirstInGroup: boolean;
  showAgentName: boolean;
  showTime: boolean;
}

function computeGroupedMessages(messages: Message[]): GroupedMessage[] {
  return messages.map((msg, i) => {
    const prev = i > 0 ? messages[i - 1] : null;
    const next = i < messages.length - 1 ? messages[i + 1] : null;

    const sameAsPrev = !!(prev && prev.from === msg.from);
    const sameAsNext = !!(next && next.from === msg.from);

    // Time gap: if more than 2 minutes apart, break the group
    const timeGapPrev = prev
      ? (new Date(msg.timestamp).getTime() - new Date(prev.timestamp).getTime()) / 1000
      : Infinity;
    const timeGapNext = next
      ? (new Date(next.timestamp).getTime() - new Date(msg.timestamp).getTime()) / 1000
      : Infinity;

    const groupedWithPrev = sameAsPrev && timeGapPrev < 120;
    const groupedWithNext = sameAsNext && timeGapNext < 120;

    const isFirstInGroup = !groupedWithPrev;
    const isLastInGroup = !groupedWithNext;

    return {
      ...msg,
      isGrouped: groupedWithPrev,
      isFirstInGroup,
      isLastInGroup,
      showAgentName: msg.from === 'agent' && isFirstInGroup && !!msg.agentName,
      showTime: isLastInGroup,
    };
  });
}

// Fallback suggestion options
const FALLBACK_SUGGESTIONS = [
  { icon: '💬', text: 'Quero falar com um atendente' },
  { icon: '❓', text: 'Tenho uma dúvida' },
  { icon: '📦', text: 'Preciso de ajuda com meu pedido' },
];

// ============================================
// Main Component
// ============================================

export function ChatContent({
  config,
  state,
  messages,
  dynamicConfig,
  assigneeInfo,
  configLoaded = true,
  chatReady = true,
  onSendMessage,
  onSendMedia,
  onRegisterPush,
  onConnect,
  onEndConversation,
  showCloseButton = false,
  onClose,
  windowPosClass = '',
  isClosing = false,
  onAfterEndConversation,
}: ChatContentProps) {
  const [inputValue, setInputValue] = useState('');
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [view, setView] = useState<WidgetView>('form');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Dynamic config with fallback to static config
  const brandColor = dynamicConfig?.brandColor || config.brandColor || '#673AB7';
  const brandDark = darkenColor(brandColor, 15);
  const agentDisplayName = assigneeInfo?.name || dynamicConfig?.agentName || config.agentName || 'Atendimento';
  const agentDisplayAvatar = assigneeInfo?.avatar || dynamicConfig?.agentAvatar || config.agentAvatar;
  const welcomeMsg = dynamicConfig?.welcomeMessage || config.welcomeMessage;
  const appName = dynamicConfig?.appName || 'TROIA';
  const rawWebsiteUrl = dynamicConfig?.appWebsiteUrl || '';
  const appWebsiteUrl = rawWebsiteUrl
    ? `${!/^https?:\/\//i.test(rawWebsiteUrl) ? `https://${rawWebsiteUrl}` : rawWebsiteUrl}?utm_source=widget&utm_medium=chat&utm_campaign=powered_by`
    : '';
  const suggestions = dynamicConfig?.suggestions?.length ? dynamicConfig.suggestions : FALLBACK_SUGGESTIONS;
  const mediaFeatures = dynamicConfig?.features ?? config.features;
  const composer = useMediaComposer(onSendMedia, mediaFeatures, state !== 'connected');
  // PWA install no embed: não instala localmente (site de terceiro) — abre a LANDING de
  // instalação da hosted page, que roda na origem da Troia e é instalável.
  // A normalização de protocolo é defensiva: o backend já normaliza (`ensureHttps`), mas
  // `app.widgetUrl` é gravado direto no Mongo e backends antigos mandavam sem esquema —
  // sem `https://`, o window.open vira navegação RELATIVA no site do cliente.
  const allowPwaInstall = mediaFeatures?.allowPwaInstall ?? false;
  const rawHostedPageUrl = dynamicConfig?.hostedPageUrl || '';
  const installUrl = rawHostedPageUrl
    ? `${!/^https?:\/\//i.test(rawHostedPageUrl) ? `https://${rawHostedPageUrl}` : rawHostedPageUrl}?install=pwa`
    : '';

  // Compute grouped messages
  const groupedMessages = useMemo(() => computeGroupedMessages(messages), [messages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when view switches to chat
  useEffect(() => {
    if (view === 'chat') {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [view]);

  const hasConfigVisitorData = !!(config.visitorName && config.visitorEmail);

  // Initialize view: if visitor data already available, go straight to chat
  useEffect(() => {
    if (state === 'connected') {
      setView('chat');
      return;
    }

    if (hasConfigVisitorData) {
      onConnect({
        name: config.visitorName!,
        email: config.visitorEmail!,
        phone: config.visitorPhone
      });
      setView('chat');
      return;
    }

    const stored = getStoredVisitorData(config.channelId);
    if (stored) {
      onConnect(stored);
      setView('chat');
      return;
    }

    setView('form');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = (visitorInfo: VisitorInfo) => {
    onConnect(visitorInfo);
    setView('chat');
  };

  const handleSend = useCallback(() => {
    if (inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue('');
      // Reset textarea height
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    }
  }, [inputValue, onSendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea and toggle multiline class
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 100);
    el.style.height = newHeight + 'px';
    // Switch to multiline mode when content wraps
    if (newHeight > 40) {
      el.classList.add('tw-input-field--multiline');
    } else {
      el.classList.remove('tw-input-field--multiline');
    }
  };

  const handleSuggestionClick = (text: string) => {
    onSendMessage(text);
  };

  // Header style - clean vertical gradient
  const headerGradient = `linear-gradient(180deg, ${brandColor} 0%, ${brandDark} 100%)`;

  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Status text
  const getStatusText = () => {
    switch (state) {
      case 'connected': return 'Online';
      case 'connecting': return 'Conectando...';
      default: return 'Offline';
    }
  };

  const getStatusClass = () => {
    switch (state) {
      case 'connected': return 'tw-header-status--online';
      case 'connecting': return 'tw-header-status--connecting';
      default: return 'tw-header-status--offline';
    }
  };

  // Bubble grouping class
  const getBubbleGroupClass = (msg: GroupedMessage) => {
    if (msg.isFirstInGroup && msg.isLastInGroup) return ''; // solo message
    if (msg.isFirstInGroup) return ' tw-msg-bubble--first';
    if (msg.isLastInGroup) return ' tw-msg-bubble--last';
    return ' tw-msg-bubble--middle';
  };

  // Wait for dynamic config to load before rendering (avoids position flash)
  if (!configLoaded) {
    return null;
  }

  // ---- Pre-Chat Form ----
  if (view === 'form') {
    return (
      <PreChatForm
        config={config}
        dynamicConfig={dynamicConfig}
        onSubmit={handleFormSubmit}
        onClose={onClose ?? (() => {})}
        isClosing={isClosing}
        headerGradient={headerGradient}
        windowPosClass={windowPosClass}
      />
    );
  }

  // ---- Chat Window ----
  return (
    <div className={`tw-window${windowPosClass}${isClosing ? ' tw-window--closing' : ''}`}>
      {/* Header */}
      <div className="tw-header" style={{ background: headerGradient }}>
        <div className="tw-header-info">
          <div className="tw-header-avatar">
            {agentDisplayAvatar ? (
              <img src={agentDisplayAvatar} alt={agentDisplayName} />
            ) : (
              <div className="tw-header-avatar-placeholder">
                <HeadphonesIcon />
              </div>
            )}
            <span className={`tw-header-status ${getStatusClass()}`} />
          </div>
          <div className="tw-header-text">
            <div className="tw-header-name">
              {agentDisplayName}
            </div>
            <div className="tw-header-subtitle">{getStatusText()}</div>
          </div>
        </div>

        {showCloseButton && (
          <div className="tw-header-actions">
            {allowPwaInstall && installUrl && (
              <span className="tw-header-btn-wrap">
                <button
                  onClick={() => window.open(installUrl, '_blank', 'noopener')}
                  className="tw-header-btn"
                  aria-label="Instalar App"
                  data-testid="widget-install-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <path d="M12 8.5v7M8.5 12h7" />
                  </svg>
                </button>
                <span className="tw-header-tooltip">Instalar App</span>
              </span>
            )}
            <button
              onClick={onClose}
              className="tw-header-btn"
              aria-label="Minimizar chat"
              title="Minimizar"
            >
              <MinimizeIcon />
            </button>
            <button
              onClick={() => {
                if (messages.length > 0) {
                  setShowEndConfirm(true);
                } else {
                  onClose?.();
                }
              }}
              className="tw-header-btn"
              aria-label={messages.length > 0 ? "Encerrar atendimento" : "Fechar"}
              title={messages.length > 0 ? "Encerrar" : "Fechar"}
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>

      {/* Push: permissão pedida SÓ por gesto; o banner só aparece se o cliente
          hospedar o troia-push-sw.js na origem dele (SW é same-origin). */}
      {view === 'chat' && state === 'connected' && (
        <EmbedPushBanner
          channelId={config.channelId}
          apiUrl={config.apiUrl}
          brandColor={brandColor}
          enabled={mediaFeatures?.allowPushNotifications ?? false}
          ready={state === 'connected'}
          {...(config.pushServiceWorkerPath ? { swPath: config.pushServiceWorkerPath } : {})}
          onSubscribed={(subscription) => onRegisterPush?.(subscription) ?? false}
        />
      )}

      {/* Connection status bar */}
      {state === 'connecting' && (
        <div className="tw-status-bar tw-status-bar--connecting">
          <span className="tw-status-bar-dot" />
          Conectando ao atendimento...
        </div>
      )}
      {state === 'disconnected' && (
        <div className="tw-status-bar tw-status-bar--disconnected">
          <span className="tw-status-bar-dot" />
          Conexão perdida. Tentando reconectar...
        </div>
      )}

      {/* Messages Area */}
      <div className="tw-messages">
        {/* Welcome Screen (no messages yet, only after chat is ready) */}
        {chatReady && messages.length === 0 && state === 'connected' && (
          <div className="tw-welcome">
            <div className="tw-welcome-greeting">
              <span
                className="tw-welcome-icon"
                style={{ background: `${brandColor}12` }}
              >
                <WelcomeIcon color={brandColor} />
              </span>
              <div className="tw-welcome-title">
                Como podemos ajudar?
              </div>
              <div className="tw-welcome-desc">
                {welcomeMsg || 'Envie uma mensagem para iniciar a conversa.'}
              </div>
            </div>

            <div className="tw-welcome-divider">
              <span className="tw-welcome-divider-line" />
              <span className="tw-welcome-divider-text">ou escolha um assunto</span>
              <span className="tw-welcome-divider-line" />
            </div>

            <div className="tw-welcome-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  className="tw-welcome-suggestion"
                  onClick={() => handleSuggestionClick(s.text)}
                >
                  <span
                    className="tw-welcome-suggestion-icon"
                    style={{ background: `${brandColor}10` }}
                  >
                    {s.icon}
                  </span>
                  <span className="tw-welcome-suggestion-text">{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message list with grouping */}
        {groupedMessages.map((message) => (
          <div
            key={message.id}
            className={`tw-msg tw-msg--${message.from === 'visitor' ? 'visitor' : 'agent'}${message.isGrouped ? ' tw-msg--grouped' : ''}`}
          >
            <div
              className={`tw-msg-bubble tw-msg-bubble--${message.from === 'visitor' ? 'visitor' : 'agent'}${getBubbleGroupClass(message)}`}
              style={message.from === 'visitor' ? { backgroundColor: brandColor } : undefined}
            >
              {message.showAgentName && (
                <div className="tw-msg-agent-name">{message.agentName}</div>
              )}
              {message.interactiveData ? (
                <InteractiveMessage
                  message={message}
                  onSendMessage={onSendMessage}
                />
              ) : isMediaMessage(message.messageType) ? (
                <MediaContent
                  message={message}
                  isVisitor={message.from === 'visitor'}
                />
              ) : (
                <div className="tw-msg-content">{message.content}</div>
              )}
              {message.showTime && (
                <div
                  className={`tw-msg-time${message.from === 'agent' ? ' tw-msg-time--agent' : ''}`}
                >
                  {formatTime(message.timestamp)}
                  {message.from === 'visitor' && <MessageStatus status={message.status} />}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* End Conversation Confirmation Modal */}
      {showEndConfirm && (
        <div className="tw-confirm-overlay">
          <div className="tw-confirm-modal">
            <div className="tw-confirm-title">Encerrar atendimento?</div>
            <div className="tw-confirm-desc">A conversa atual será finalizada.</div>
            <div className="tw-confirm-actions">
              <button
                className="tw-confirm-btn-cancel"
                onClick={() => setShowEndConfirm(false)}
              >
                Cancelar
              </button>
              <button
                className="tw-confirm-btn-end"
                style={{ backgroundColor: brandColor }}
                onClick={() => {
                  setShowEndConfirm(false);
                  onEndConversation();
                  onAfterEndConversation?.();
                }}
              >
                Encerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="tw-input-area">
        {composer.showAttach && onSendMedia && (
          <input
            ref={composer.fileInputRef}
            type="file"
            accept={composer.acceptTypes}
            onChange={composer.handleFileSelected}
            style={{ display: 'none' }}
          />
        )}

        {composer.recording ? (
          <div className="tw-recording-bar">
            <span className="tw-recording-dot" />
            <span className="tw-recording-label">Gravando...</span>
            <span className="tw-recording-time">
              {composer.formatRecordingTime(composer.recordingSeconds)}
            </span>
            <button
              type="button"
              onClick={composer.cancelRecording}
              className="tw-recording-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={composer.stopAndSendAudio}
              className="tw-send-btn"
              style={{ backgroundColor: brandColor }}
              aria-label="Parar e enviar"
            >
              <SendIcon />
            </button>
          </div>
        ) : (
          <div className="tw-input-wrapper">
            {composer.showAttach && onSendMedia && (
              <button
                type="button"
                onClick={composer.openFilePicker}
                disabled={state !== 'connected' || composer.uploading}
                className="tw-attach-btn"
                aria-label="Anexar arquivo"
                title="Anexar"
              >
                <PaperclipIcon />
              </button>
            )}
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={composer.uploading ? 'Enviando...' : 'Escreva sua mensagem...'}
              className="tw-input-field"
              rows={1}
              disabled={state !== 'connected' || composer.uploading}
            />
            {inputValue.trim() ? (
              <button
                onClick={handleSend}
                disabled={state !== 'connected' || composer.uploading}
                className="tw-send-btn"
                style={{ backgroundColor: brandColor }}
                aria-label="Enviar mensagem"
              >
                <SendIcon />
              </button>
            ) : composer.showAudio && onSendMedia ? (
              <button
                onClick={composer.startRecording}
                disabled={state !== 'connected' || composer.uploading}
                className="tw-send-btn"
                style={{ backgroundColor: brandColor }}
                aria-label="Gravar áudio"
              >
                <MicIcon />
              </button>
            ) : (
              <button
                disabled
                className="tw-send-btn"
                style={{ backgroundColor: '#d1d5db' }}
                aria-label="Enviar mensagem"
              >
                <SendIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Powered by */}
      <div className="tw-powered">
        <span className="tw-powered-text">
          Desenvolvido por {appWebsiteUrl ? (
            <a href={appWebsiteUrl} target="_blank" rel="noopener noreferrer" className="tw-powered-link">{appName}</a>
          ) : (
            <span className="tw-powered-link">{appName}</span>
          )}
        </span>
      </div>
    </div>
  );
}
